import { rawFetchJson, rawFetchFormData } from './api';
import type { Redesign, RedesignTask } from '../types';

interface RedesignListResponse {
  redesigns: Redesign[];
  total: number;
  page: number;
  limit: number;
}

export async function fetchRedesigns(
  filters?: { repo?: string; status?: string },
  page = 1,
  limit = 20
): Promise<RedesignListResponse> {
  const params = new URLSearchParams({ page: String(page), limit: String(limit) });
  if (filters?.repo) params.set('repo', filters.repo);
  if (filters?.status) params.set('status', filters.status);

  return rawFetchJson<RedesignListResponse>(`/redesigns?${params}`);
}

export async function fetchRedesign(id: string): Promise<Redesign> {
  return rawFetchJson<Redesign>(`/redesigns/${id}`);
}

export async function createRedesign(
  serviceProfileId: string,
  options?: { name?: string; authStrategy?: string; mode?: string; referenceRoutes?: string[]; startPath?: string; pathPrefix?: string }
): Promise<{ message: string; idempotencyKey: string }> {
  return rawFetchJson(`/redesigns`, {
    method: 'POST',
    body: JSON.stringify({
      serviceProfileId,
      name: options?.name,
      authStrategy: options?.authStrategy,
      mode: options?.mode,
      referenceRoutes: options?.referenceRoutes,
      startPath: options?.startPath,
      pathPrefix: options?.pathPrefix,
    }),
  });
}

export async function updateRedesign(
  id: string,
  updates: { name?: string; prompt?: string }
): Promise<{ message: string }> {
  return rawFetchJson(`/redesigns/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(updates),
  });
}

export async function cancelCapture(
  redesignId: string
): Promise<{ message: string }> {
  return rawFetchJson(`/redesigns/${redesignId}/cancel-capture`, {
    method: 'POST',
  });
}

export async function startCapture(
  redesignId: string,
  options?: { authStrategy?: string; startPath?: string; pathPrefix?: string }
): Promise<{ message: string; idempotencyKey: string }> {
  return rawFetchJson(`/redesigns/${redesignId}/start-capture`, {
    method: 'POST',
    body: JSON.stringify({
      authStrategy: options?.authStrategy,
      startPath: options?.startPath,
      pathPrefix: options?.pathPrefix,
    }),
  });
}

export async function uploadPages(
  redesignId: string,
  files: File[],
  pages: Array<{ route: string; viewport: string }>
): Promise<{ message: string; addedCount: number }> {
  const formData = new FormData();
  for (const file of files) {
    formData.append('screenshots', file);
  }
  formData.append('pages', JSON.stringify(pages));
  return rawFetchFormData(`/redesigns/${redesignId}/upload-pages`, formData);
}

export async function updatePage(
  id: string,
  pageIndex: number,
  updates: { comment?: string; excluded?: boolean; route?: string; viewport?: string }
): Promise<void> {
  await rawFetchJson(`/redesigns/${id}/pages/${pageIndex}`, {
    method: 'PATCH',
    body: JSON.stringify(updates),
  });
}

export async function analyzeWithGemini(
  id: string
): Promise<{ message: string; succeeded: number; failed: number }> {
  return rawFetchJson(`/redesigns/${id}/analyze`, {
    method: 'POST',
  });
}

export async function deleteRedesign(id: string): Promise<{ message: string }> {
  return rawFetchJson(`/redesigns/${id}`, { method: 'DELETE' });
}

export async function analyzePageWithGemini(
  id: string,
  pageIndex: number
): Promise<{ message: string; status: string }> {
  return rawFetchJson(`/redesigns/${id}/pages/${pageIndex}/analyze`, {
    method: 'POST',
  });
}

// --- Figma MCP functions ---

export async function fetchFigmaMcpStatus(): Promise<{ enabled: boolean; user?: { email: string; plan?: string }; error?: string }> {
  return rawFetchJson('/redesigns/figma-mcp/status');
}

export async function fetchFigmaContext(
  redesignId: string,
  pageIndex: number,
  figmaUrls: string | string[]
): Promise<{ figmaContext: string; figmaScreenshotUrl?: string; figmaImports?: Array<{ fileKey: string; nodeId: string; screenshotUrl?: string; label?: string }>; status: string; importCount: number }> {
  const body = Array.isArray(figmaUrls)
    ? { figmaUrls }
    : { figmaUrl: figmaUrls };
  return rawFetchJson(`/redesigns/${redesignId}/pages/${pageIndex}/figma-context`, {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export async function submitTaskToFigma(
  redesignId: string,
  taskId: string,
  targetFileKey: string
): Promise<{ message: string; idempotencyKey: string }> {
  return rawFetchJson(`/redesigns/${redesignId}/tasks/${taskId}/submit-figma`, {
    method: 'POST',
    body: JSON.stringify({ targetFileKey }),
  });
}

export async function pushTaskToFigma(
  redesignId: string,
  taskId: string,
  targetFileKey: string,
  pageName?: string
): Promise<{ message: string; nodeId?: string }> {
  return rawFetchJson(`/redesigns/${redesignId}/tasks/${taskId}/push-to-figma`, {
    method: 'POST',
    body: JSON.stringify({ targetFileKey, pageName }),
  });
}

export async function createDesignerTicket(
  redesignId: string,
  taskId: string,
  designerGuidance?: string,
  title?: string
): Promise<{ issueUrl: string; issueIdentifier: string; message: string }> {
  return rawFetchJson(`/redesigns/${redesignId}/tasks/${taskId}/create-designer-ticket`, {
    method: 'POST',
    body: JSON.stringify({ designerGuidance, title }),
  });
}

export async function fetchDesignsFromTicket(
  redesignId: string,
  taskId: string
): Promise<{ figmaUrls: string[]; importCount: number; status: string; message: string }> {
  return rawFetchJson(`/redesigns/${redesignId}/tasks/${taskId}/fetch-from-ticket`, {
    method: 'POST',
  });
}

// --- Task functions ---

export async function fetchTasks(
  redesignId: string
): Promise<RedesignTask[]> {
  const data = await rawFetchJson<{ tasks: RedesignTask[] }>(`/redesigns/${redesignId}/tasks`);
  return data.tasks;
}

export async function fetchTask(
  redesignId: string,
  taskId: string
): Promise<RedesignTask> {
  return rawFetchJson<RedesignTask>(`/redesigns/${redesignId}/tasks/${taskId}`);
}

export async function createTask(
  redesignId: string,
  body: { pageRef: { route: string; viewport: string }; designerNotes?: string }
): Promise<RedesignTask> {
  return rawFetchJson<RedesignTask>(`/redesigns/${redesignId}/tasks`, {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export async function updateTask(
  redesignId: string,
  taskId: string,
  updates: { designerNotes: string }
): Promise<void> {
  await rawFetchJson(`/redesigns/${redesignId}/tasks/${taskId}`, {
    method: 'PATCH',
    body: JSON.stringify(updates),
  });
}

export async function deleteTask(
  redesignId: string,
  taskId: string
): Promise<void> {
  await rawFetchJson(`/redesigns/${redesignId}/tasks/${taskId}`, {
    method: 'DELETE',
  });
}

export async function submitTask(
  redesignId: string,
  taskId: string
): Promise<{ message: string }> {
  return rawFetchJson(`/redesigns/${redesignId}/tasks/${taskId}/submit`, {
    method: 'POST',
  });
}

export async function fetchTaskFromStitch(
  redesignId: string,
  taskId: string
): Promise<{ message: string }> {
  return rawFetchJson(`/redesigns/${redesignId}/tasks/${taskId}/fetch-stitch`, {
    method: 'POST',
  });
}

export async function selectTaskVariant(
  redesignId: string,
  taskId: string,
  variantIndex: number
): Promise<void> {
  await rawFetchJson(`/redesigns/${redesignId}/tasks/${taskId}/select-variant`, {
    method: 'POST',
    body: JSON.stringify({ variantIndex }),
  });
}

export async function unlinkTaskIssue(
  redesignId: string,
  taskId: string
): Promise<{ message: string }> {
  return rawFetchJson(`/redesigns/${redesignId}/tasks/${taskId}/unlink-issue`, {
    method: 'DELETE',
  });
}

export async function createTaskIssue(
  redesignId: string,
  taskId: string,
  engineerNotes?: string,
  title?: string
): Promise<{ issueUrl: string; issueIdentifier: string; message: string }> {
  return rawFetchJson(`/redesigns/${redesignId}/tasks/${taskId}/create-issue`, {
    method: 'POST',
    body: JSON.stringify({ engineerNotes, title }),
  });
}

// --- Design System functions ---

export async function createDesignSystemRedesign(
  serviceProfileId: string
): Promise<{ message: string; idempotencyKey?: string; redesignId?: string; existing?: boolean }> {
  return rawFetchJson(`/redesigns`, {
    method: 'POST',
    body: JSON.stringify({
      serviceProfileId,
      mode: 'design_system',
    }),
  });
}

export async function resetTaskFigma(
  redesignId: string,
  taskId: string
): Promise<{ message: string }> {
  return rawFetchJson(`/redesigns/${redesignId}/tasks/${taskId}/reset-figma`, {
    method: 'POST',
  });
}

export async function syncDesignSystem(
  redesignId: string,
  taskId: string
): Promise<{ message: string; variableCount: number; propagationStatus: string }> {
  return rawFetchJson(`/redesigns/${redesignId}/tasks/${taskId}/sync-design-system`, {
    method: 'POST',
  });
}
